'use client';

import { useState } from 'react';
import { useSnackbar } from '@ghosts-inc/design-system/hooks';
import { ThumbsDown } from '@ghosts-inc/design-system/icons';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { AnalyticsEventNames } from '@/analytics/Event.types';
import { PageContext, SectionContext } from '@/analytics/analytics.types';
import { analytics } from '@/analytics/segment';
import { dislikeListing as dislikeListingApi } from '@/app/(authenticated)/(marketplace)/actions';
import { BuyerListingDislikeReason } from '@/app/(authenticated)/(marketplace)/types';
import CustomFeedbackDialog from './CustomFeedbackDialog';

type Props = {
  analyticsInfo?: { context?: PageContext & SectionContext };
  listingId: number;
};

const preventPropagation = (event: React.MouseEvent<HTMLElement>) => {
  event.stopPropagation();
  event.preventDefault();
};

export default function DislikeButton({ analyticsInfo, listingId }: Props): JSX.Element {
  const { showSnackbar } = useSnackbar();
  const [isDisliked, setIsDisliked] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCustomFeedbackDialogOpen, setIsCustomFeedbackDialogOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleDislikeClick = (event: React.MouseEvent<HTMLElement>) => {
    preventPropagation(event);
    if (isDisliked) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: React.MouseEvent<HTMLElement>) => {
    preventPropagation(event);
    setAnchorEl(null);
  };

  const dislikeListing = async ({
    customReason,
    reasonType,
  }: {
    customReason: string | null;
    reasonType: BuyerListingDislikeReason;
  }) => {
    setIsSubmitting(true);
    const { success } = await dislikeListingApi({ listingId, reasonType, customReason });

    if (success) {
      analytics.track(AnalyticsEventNames.LISTING_DISLIKED, {
        listing_id: listingId,
        ...analyticsInfo,
      });
      setIsDisliked(true);
      showSnackbar({ action: <></>, severity: 'alertSuccess', title: 'Thanks for your feedback!' });
    } else {
      showSnackbar({ severity: 'alertError', title: 'Something went wrong.' });
    }
    setIsSubmitting(false);
  };

  const handleReasonSelect = async (
    reasonType: BuyerListingDislikeReason,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    preventPropagation(event);
    if (reasonType === BuyerListingDislikeReason.OTHER) {
      setIsCustomFeedbackDialogOpen(true);
    } else {
      await dislikeListing({ reasonType, customReason: null });
    }
    setAnchorEl(null);
  };

  return (
    // Prevent the click event from bubbling up to the parent. This is necessary to avoid being redirected to the listing page
    // when the user clicks inside the menu or the custom feedback dialog.
    <Box onClick={(e) => e.stopPropagation()}>
      <IconButton
        aria-controls={open ? 'dislike-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        aria-label={isDisliked ? 'Disliked listing' : 'Dislike listing'}
        disabled={isSubmitting || isDisliked}
        onClick={handleDislikeClick}
        sx={(theme) => ({
          p: { mobile: 1.5, tablet: 2 },
          bgcolor: 'surfaceLight.soft',
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
          '&:hover svg': { fill: theme.palette.surfaceDark.soft },
        })}
      >
        <ThumbsDown sx={{ fontSize: { mobile: 16, tablet: 20 }, color: 'surfaceDark.soft' }} />
      </IconButton>
      <Menu
        id="dislike-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'dislike-button', dense: true }}
      >
        <Typography variant="labelSmall" color="typographyLightSecondary" px={1} mt={1.5} mb={1}>
          I&apos;m not interested
        </Typography>
        {Object.values(BuyerListingDislikeReason).map((reason) => (
          <MenuItem key={reason} onClick={(event) => handleReasonSelect(reason, event)}>
            {reason}
          </MenuItem>
        ))}
      </Menu>
      <CustomFeedbackDialog
        isOpen={isCustomFeedbackDialogOpen}
        onClose={() => setIsCustomFeedbackDialogOpen(false)}
        onSubmit={dislikeListing}
      />
    </Box>
  );
}
